import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Checkbox,
  Grid,
  MultiSelect,
  Paper,
  Select,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFileSpreadsheet, IconRefresh } from '@tabler/icons-react';
import { MRT_PaginationState } from 'mantine-react-table';
import momentTz from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { OrderHistoryDateField, OrderHistoryStatus } from 'washday-sdk';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import { DEFAULT_FOLIO_PREFIX } from '../../../constants';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { useGetOrdersHistoryReport } from '../../../hooks/reports/queries';
import { exportOrdersHistoryReport } from '../../../services/reports.service';
import { handleOrderHistoryReportExport } from './exportHandler';
import { buildOrderHistoryReportExportParams } from './exportParams';
import {
  applyOrderHistoryFilterChange,
  buildOrderHistoryReportQuery,
  createDefaultOrderHistoryReportState,
  getOrderHistoryDateFieldOptions,
  OrderHistoryFilters,
  orderHistoryStatuses,
  resetOrderHistoryPagination,
  resetOrderHistoryReportState
} from './filters';
import { buildOrderHistoryReportRows } from './reportRows';
import TableView from './TableView';

const defaultSummary = {
  totalOrders: 0,
  pickupOrders: 0,
  deliveryOrders: 0,
  pickupAndDeliveryOrders: 0
};

const formatCount = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value || 0);

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <Grid.Col span={{ xs: 12, sm: 6, md: 2 }}>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text fw={700}>{formatCount(value)}</Text>
    </Grid.Col>
  );
}

const OrdersHistoryReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons']);
  const location = useLocation();
  const {
    company: { _id: companyId }
  } = useSelector((state: { user: { company: { _id: string } } }) => state.user);
  const searchParams = new URLSearchParams(location.search);
  const storeId = searchParams.get('storeId') || 'all';
  const timezone = useMemo(() => momentTz.tz.guess(true), []);
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [reportState, setReportState] = useState(createDefaultOrderHistoryReportState);

  useEffect(() => {
    setReportState(resetOrderHistoryPagination);
  }, [storeId]);

  const updateFilters = useCallback((change: Partial<OrderHistoryFilters>) => {
    setReportState((current) => applyOrderHistoryFilterChange(current, change));
  }, []);

  const setPagination: Dispatch<SetStateAction<MRT_PaginationState>> = useCallback(
    (nextPagination) => {
      setReportState((current) => ({
        ...current,
        pagination:
          typeof nextPagination === 'function' ? nextPagination(current.pagination) : nextPagination
      }));
    },
    []
  );

  const query = useMemo(
    () => buildOrderHistoryReportQuery(reportState, storeId),
    [reportState, storeId]
  );
  const exportQuery = useMemo(
    () => buildOrderHistoryReportExportParams(query, timezone),
    [query, timezone]
  );
  const dateFieldOptions = useMemo(
    () =>
      getOrderHistoryDateFieldOptions(reportState.filters.homeServiceOnly).map((field) => ({
        value: field,
        label: t(`ReportsPage:ordersHistoryReport.dateFields.${field}`)
      })),
    [reportState.filters.homeServiceOnly, t]
  );
  const statusOptions = useMemo(
    () =>
      orderHistoryStatuses.map((status) => ({
        value: status,
        label: t(`FieldName:orderStatuses.${status}`)
      })),
    [t]
  );

  const { data, isLoading, isFetching, isError } = useGetOrdersHistoryReport(companyId, query);
  const summary = data?.summary || defaultSummary;
  const rows = useMemo(() => buildOrderHistoryReportRows(data?.items || []), [data?.items]);

  return (
    <CatalogContainer pos="relative">
      <CatalogListHeader
        title={t('ReportsPage:ordersHistoryReport.title')}
        showCreateButton={false}
      />
      <Text c="dimmed" size="sm" mb="sm">
        {t('ReportsPage:ordersHistoryReport.subtitle')}
      </Text>
      <LoaderOverlay visible={isLoading || isFetching} />
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <Select
            label={t('ReportsPage:ordersHistoryReport.filters.dateField')}
            data={dateFieldOptions}
            value={reportState.filters.dateField}
            onChange={(value) =>
              updateFilters({ dateField: (value || 'created') as OrderHistoryDateField })
            }
            allowDeselect={false}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('ReportsPage:ordersHistoryReport.filters.dateRange')}
            placeholder={t('ReportsPage:ordersHistoryReport.filters.dateRange')}
            value={reportState.filters.dateRange}
            onChange={(dateRange: DatesRangeValue) => updateFilters({ dateRange })}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <MultiSelect
            label={t('ReportsPage:ordersHistoryReport.filters.statuses')}
            data={statusOptions}
            value={reportState.filters.statuses}
            onChange={(statuses) => updateFilters({ statuses: statuses as OrderHistoryStatus[] })}
            clearable
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <TextInput
            type="number"
            label={t('ReportsPage:ordersHistoryReport.filters.sequence')}
            value={reportState.filters.sequence}
            leftSection={`${DEFAULT_FOLIO_PREFIX}-`}
            onChange={(event) => updateFilters({ sequence: event.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 4 }} style={{ alignSelf: 'flex-end' }}>
          <Checkbox
            label={t('ReportsPage:ordersHistoryReport.filters.homeServiceOnly')}
            checked={reportState.filters.homeServiceOnly}
            onChange={(event) => updateFilters({ homeServiceOnly: event.currentTarget.checked })}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
          <Button
            fullWidth
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={() => setReportState(resetOrderHistoryReportState())}
            type="button"
          >
            {t('CommonButtons:clear').toUpperCase()}
          </Button>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport || !companyId || !storeId}
              onClick={() => {
                void handleOrderHistoryReportExport({
                  downloadExcel: downloadExcelV2,
                  fetchReport: () => exportOrdersHistoryReport(companyId, exportQuery),
                  filename: `${t('ReportsPage:ordersHistoryReport.exportFilename')}_${storeId}`
                });
              }}
              variant="filled"
              color="#1D6F42"
              aria-label={t('CommonButtons:exportList')}
            >
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper withBorder p="sm" radius="md">
            <Grid gutter="xs">
              <SummaryMetric
                label={t('ReportsPage:ordersHistoryReport.summary.orders')}
                value={summary.totalOrders}
              />
              {reportState.filters.homeServiceOnly && (
                <>
                  <SummaryMetric
                    label={t('ReportsPage:ordersHistoryReport.summary.homeOrders')}
                    value={summary.totalOrders}
                  />
                  <SummaryMetric
                    label={t('ReportsPage:ordersHistoryReport.summary.pickups')}
                    value={summary.pickupOrders}
                  />
                  <SummaryMetric
                    label={t('ReportsPage:ordersHistoryReport.summary.deliveries')}
                    value={summary.deliveryOrders}
                  />
                  <SummaryMetric
                    label={t('ReportsPage:ordersHistoryReport.summary.pickupAndDelivery')}
                    value={summary.pickupAndDeliveryOrders}
                  />
                </>
              )}
            </Grid>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <TableView
            tableInfoData={rows}
            totalRowsCount={data?.pagination.totalRows || 0}
            pages={data?.pagination.totalPages || 0}
            isError={isError}
            isLoading={isLoading || isFetching}
            pagination={reportState.pagination}
            setPagination={setPagination}
            dateField={reportState.filters.dateField}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default OrdersHistoryReport;
